import { type RefObject, useLayoutEffect } from 'react';

// dataset keys are camelCase; the attribute Tailwind matches is kebab-case
// (`fitCompact` -> `data-fit-compact`).
export const INBOX_TOOLBAR_FIT_STEPS = ['fitCompact', 'fitTight'] as const;

/**
 * Keeps the filter row on one line when its text outgrows the column.
 *
 * Browser zoom-out with a minimum font size holds the pill labels at a fixed
 * device size while the column shrinks with the page, so on a narrow window
 * the last pill is clipped. Nothing in CSS can observe that, so this measures.
 *
 * It never measures the live row. Each step is tried on a hidden copy of
 * the row, sized to the same width, and the first copy that fits decides.
 * The live row only gets a `data-fit-*` attribute written when the answer
 * changes, and nothing observes its children, so the row cannot oscillate.
 */
export function useFitToolbar(
    ref: RefObject<Element | null>,
    steps: ReadonlyArray<string>,
) {
    useLayoutEffect(() => {
        const element = ref.current;
        if (!(element instanceof HTMLElement)) {
            return;
        }
        const toolbar: HTMLElement = element;
        function neededSteps(): number {
            const host = document.createElement('div');
            host.style.cssText =
                'position:absolute;left:0;top:0;height:0;overflow:hidden;visibility:hidden;pointer-events:none';
            host.style.width = `${toolbar.clientWidth}px`;
            toolbar.parentElement?.appendChild(host);
            try {
                for (let count = 0; count <= steps.length; count += 1) {
                    const copy = toolbar.cloneNode(true) as HTMLElement;
                    for (const step of steps) {
                        delete copy.dataset[step];
                    }
                    for (const step of steps.slice(0, count)) {
                        copy.dataset[step] = '';
                    }
                    copy.style.width = `${toolbar.clientWidth}px`;
                    host.replaceChildren(copy);
                    if (copy.scrollWidth <= copy.clientWidth) {
                        return count;
                    }
                }
                return steps.length;
            } finally {
                host.remove();
            }
        }
        function fit() {
            const count = neededSteps();
            steps.forEach((step, index) => {
                const wanted = index < count;
                if (wanted && !(step in toolbar.dataset)) {
                    toolbar.dataset[step] = '';
                } else if (!wanted && step in toolbar.dataset) {
                    delete toolbar.dataset[step];
                }
            });
        }
        fit();
        // Column resizes and browser zoom arrive as these; content changes
        // arrive as renders, which re-run this effect.
        const observer = new ResizeObserver(fit);
        observer.observe(toolbar);
        window.addEventListener('resize', fit);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', fit);
        };
    });
}
